import { supabase } from '../lib/supabase'
import type { EmailDispatch, EmailDispatchStatus } from '../types/emailDispatches'
import { type EmailSystem, normalizeEmailSystem } from '../types/emailTemplates'

const DEFAULT_LIMIT = 500
/** Bucket Supabase Storage dos HTMLs efetivamente enviados */
export const EMAIL_SENT_STORAGE_BUCKET = 'email_sent'

function localDayEnd(yyyyMmDd: string): Date {
  const [y, m, d] = yyyyMmDd.split('-').map((p) => parseInt(p, 10))
  return new Date(y, m - 1, d, 23, 59, 59, 999)
}

export function extractStorageErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message
  if (error && typeof error === 'object') {
    const o = error as { message?: string; error?: string; statusCode?: string }
    if (typeof o.message === 'string' && o.message.trim()) return o.message
    if (typeof o.error === 'string' && o.error.trim()) return o.error
    if (o.statusCode) return `${fallback} (HTTP ${o.statusCode})`
  }
  return fallback
}

/** Caminho do objeto dentro do bucket email_sent (sem prefixo do bucket). */
export function resolveSentHtmlObjectPath(row: EmailDispatch): string | null {
  const raw = row.sent_file_path?.trim()
  if (!raw) return null
  let path = raw.replace(/^\/+/, '')
  if (path.startsWith(`${EMAIL_SENT_STORAGE_BUCKET}/`)) {
    path = path.slice(EMAIL_SENT_STORAGE_BUCKET.length + 1)
  }
  return path || null
}

function triggerHtmlDownload(html: string, fileName: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download =
      fileName.endsWith('.html') || fileName.endsWith('.htm') ? fileName : `${fileName}.html`
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

export const emailDispatchesService = {
  async list(filters?: {
    system?: EmailSystem | ''
    status?: EmailDispatchStatus | ''
    dateFrom?: string
    dateTo?: string
    recipient?: string
    sourceId?: string
    limit?: number
  }): Promise<EmailDispatch[]> {
    let query = supabase
      .from('email_dispatches')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(filters?.limit ?? DEFAULT_LIMIT)

    if (filters?.system) {
      query = query.eq('system', normalizeEmailSystem(filters.system))
    }
    if (filters?.status) {
      query = query.eq('status', filters.status)
    }
    if (filters?.dateFrom) {
      query = query.gte('created_at', new Date(filters.dateFrom).toISOString())
    }
    if (filters?.dateTo) {
      query = query.lte('created_at', localDayEnd(filters.dateTo).toISOString())
    }
    if (filters?.recipient?.trim()) {
      query = query.ilike('recipient', `%${filters.recipient.trim()}%`)
    }
    if (filters?.sourceId?.trim()) {
      query = query.ilike('source_id', `%${filters.sourceId.trim()}%`)
    }

    const { data, error } = await query
    if (error) throw error

    return (data || []).map((row) => ({
      ...row,
      system: normalizeEmailSystem(row.system),
    })) as EmailDispatch[]
  },

  async downloadSentHtml(row: EmailDispatch): Promise<void> {
    const path = resolveSentHtmlObjectPath(row)
    if (!path) {
      throw new Error('Caminho do HTML enviado (sent_file_path) não informado.')
    }

    const bucket = EMAIL_SENT_STORAGE_BUCKET
    const fileName =
      path.split('/').filter(Boolean).pop() || `email-enviado-${row.id.slice(0, 8)}.html`

    const { data: blob, error: downloadError } = await supabase.storage.from(bucket).download(path)

    if (!downloadError && blob) {
      const html = await blob.text()
      triggerHtmlDownload(html, fileName)
      return
    }

    const { data: signed, error: signError } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 300)

    if (!signError && signed?.signedUrl) {
      const res = await fetch(signed.signedUrl)
      if (!res.ok) {
        throw new Error(`Falha ao baixar HTML (HTTP ${res.status}).`)
      }
      const html = await res.text()
      triggerHtmlDownload(html, fileName)
      return
    }

    const detail = extractStorageErrorMessage(
      downloadError || signError,
      'Não foi possível baixar o HTML do bucket email_sent. Verifique sent_file_path e as políticas de Storage (configuracoes_email_08_storage_email_sent.sql).'
    )
    throw new Error(detail)
  },
}
